import { useEffect, useRef, useState } from "react";
import { api, fileSrc } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Paperclip, Send, Image as ImageIcon, Video, X, Plus, Hash, Lock, Trash2, Heart, Reply } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { playChime } from "@/lib/sound";
import { formatMessageTime } from "@/lib/time";

export default function Chat() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "owner";
  const isOwner = user?.role === "owner";
  const readOnly = !!user?.read_only;
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState("general");
  const [showNewChannel, setShowNewChannel] = useState(false);
  const [newChannelName, setNewChannelName] = useState("");
  const [newChannelOwnerOnly, setNewChannelOwnerOnly] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showVideoInput, setShowVideoInput] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);
  const initialScrollRef = useRef(false);
  const lastSinceRef = useRef(null);
  const activeChannelRef = useRef("general");

  const loadChannels = async () => {
    try {
      const { data } = await api.get("/chat/channels");
      setChannels(data);
    } catch (err) { console.error("Failed to load channels:", err); }
  };

  const loadAll = async (cid) => {
    const { data } = await api.get(`/chat?channel_id=${encodeURIComponent(cid)}`);
    setMessages(data);
    lastSinceRef.current = data.length ? data[data.length - 1].created_at : null;
  };

  const poll = async () => {
    const cid = activeChannelRef.current;
    if (!lastSinceRef.current) return;
    try {
      const { data } = await api.get(`/chat?channel_id=${encodeURIComponent(cid)}&since=${encodeURIComponent(lastSinceRef.current)}`);
      if (data.length) {
        setMessages((prev) => [...prev, ...data]);
        lastSinceRef.current = data[data.length - 1].created_at;
        if (data.some((m) => m.author_id !== user?.user_id)) playChime();
      }
    } catch (err) {
      console.error("Chat poll failed:", err);
    }
  };

  useEffect(() => { loadChannels(); }, []);

  useEffect(() => {
    activeChannelRef.current = activeChannel;
    lastSinceRef.current = null;
    initialScrollRef.current = false;
    loadAll(activeChannel);
    const t = setInterval(poll, 1500);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChannel]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      if (!initialScrollRef.current) {
        el.scrollTop = el.scrollHeight;
        initialScrollRef.current = true;
      } else {
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      }
    });
  }, [messages]);

  const createChannel = async () => {
    const name = newChannelName.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/chat/channels", { name, is_owner_only: newChannelOwnerOnly });
      setNewChannelName(""); setShowNewChannel(false); setNewChannelOwnerOnly(false);
      await loadChannels();
      setActiveChannel(data.id);
      toast.success(data.is_owner_only ? "Owner-only channel created" : "Channel created");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create channel");
    }
  };

  const removeChannel = async (cid, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this channel and all its messages?")) return;
    try {
      await api.delete(`/chat/channels/${cid}`);
      await loadChannels();
      if (activeChannel === cid) setActiveChannel("general");
      toast.success("Channel deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete channel");
    }
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachment(data);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() && !attachment && !videoUrl.trim()) return;
    try {
      const { data } = await api.post("/chat", {
        text: text.trim(),
        attachment_file_id: attachment?.id || null,
        video_url: videoUrl.trim() || null,
        channel_id: activeChannel,
        reply_to: replyTo?.id || null,
      });
      setMessages((prev) => [...prev, data]);
      lastSinceRef.current = data.created_at;
      setText(""); setAttachment(null); setVideoUrl(""); setShowVideoInput(false); setReplyTo(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm("Delete this message for everyone?")) return;
    try {
      await api.delete(`/chat/${id}`);
      setMessages((prev) => prev.filter((m) => m.id !== id));
      toast.success("Message deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete message");
    }
  };

  const toggleLike = async (id) => {
    // optimistic
    setMessages((prev) => prev.map((m) => {
      if (m.id !== id) return m;
      const liked = (m.liked_by || []).includes(user?.user_id);
      const liked_by = liked
        ? (m.liked_by || []).filter((u) => u !== user?.user_id)
        : [...(m.liked_by || []), user?.user_id];
      return { ...m, liked_by };
    }));
    try {
      await api.post(`/chat/${id}/like`);
    } catch (err) {
      // revert optimistic update on failure
      setMessages((prev) => prev.map((m) => {
        if (m.id !== id) return m;
        const liked = (m.liked_by || []).includes(user?.user_id);
        const liked_by = liked
          ? (m.liked_by || []).filter((u) => u !== user?.user_id)
          : [...(m.liked_by || []), user?.user_id];
        return { ...m, liked_by };
      }));
      toast.error(err.response?.data?.detail || "Failed to like");
    }
  };

  const startReply = (m) => {
    setReplyTo({ id: m.id, author_name: m.author_name, text: m.text });
    inputRef.current?.focus();
  };

  const getYoutubeEmbed = (url) => {
    const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
    return m ? `https://www.youtube.com/embed/${m[1]}` : null;
  };

  return (
    <div className="relative flex flex-col h-[calc(100vh-9rem)] bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center">
        <img src="/cfaa-mark.png" alt="" className="w-full h-full opacity-100 object-cover" />
      </div>
      <div className="relative z-10 px-4 sm:px-6 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2 flex-wrap" data-testid="chat-channel-bar">
          {channels.map((ch) => (
            <button
              key={ch.id}
              onClick={() => setActiveChannel(ch.id)}
              data-testid={`chat-channel-${ch.id}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                activeChannel === ch.id ? "bg-purple-700 text-white" : "text-slate-600 bg-slate-100 hover:bg-slate-200"
              }`}
            >
              <Hash className="w-3.5 h-3.5" />
              {ch.name}
              {ch.is_owner_only && <Lock className="w-3 h-3 opacity-80" />}
              {isAdmin && ch.id !== "general" && (
                <span
                  onClick={(e) => removeChannel(ch.id, e)}
                  data-testid={`chat-channel-delete-${ch.id}`}
                  className={`ml-1 rounded-full p-0.5 cursor-pointer ${activeChannel === ch.id ? "hover:bg-purple-900" : "hover:bg-slate-300"}`}
                >
                  <X className="w-3 h-3" />
                </span>
              )}
            </button>
          ))}
          {isAdmin && (
            showNewChannel ? (
              <span className="inline-flex items-center gap-2 flex-wrap">
                <Input
                  autoFocus
                  value={newChannelName}
                  onChange={(e) => setNewChannelName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); createChannel(); } }}
                  placeholder="channel name"
                  data-testid="chat-new-channel-input"
                  className="h-8 w-36 rounded-full text-sm"
                />
                {isOwner && (
                  <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-pointer select-none" data-testid="chat-owner-only-label">
                    <input
                      type="checkbox"
                      checked={newChannelOwnerOnly}
                      onChange={(e) => setNewChannelOwnerOnly(e.target.checked)}
                      data-testid="chat-owner-only-checkbox"
                      className="accent-purple-700 w-4 h-4"
                    />
                    <Lock className="w-3 h-3" /> Owner only
                  </label>
                )}
                <Button type="button" onClick={createChannel} data-testid="chat-create-channel-btn" className="h-8 px-3 rounded-full bg-purple-700 hover:bg-purple-800 text-white text-xs">Add</Button>
                <button type="button" onClick={() => { setShowNewChannel(false); setNewChannelName(""); setNewChannelOwnerOnly(false); }} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setShowNewChannel(true)}
                data-testid="chat-add-channel-btn"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-semibold text-purple-700 border border-dashed border-purple-300 hover:bg-purple-50"
              >
                <Plus className="w-3.5 h-3.5" /> Channel
              </button>
            )
          )}
        </div>
      </div>

      <div ref={scrollRef} className="relative z-10 flex-1 overflow-y-auto chat-scroll p-6 space-y-4" data-testid="chat-message-list">
        {messages.length === 0 && (
          <div className="text-center text-slate-400 py-12">
            <p className="text-lg font-semibold">Say hi to the group 👋</p>
          </div>
        )}
        {messages.map((m) => {
          const mine = m.author_id === user?.user_id;
          const yt = m.video_url ? getYoutubeEmbed(m.video_url) : null;
          const canDelete = isOwner;
          const likeCount = (m.liked_by || []).length;
          const liked = (m.liked_by || []).includes(user?.user_id);
          return (
            <div key={m.id} className={`group flex gap-3 animate-slide-in ${mine ? "flex-row-reverse" : ""}`}>
              <Avatar className="w-9 h-9 flex-shrink-0">
                <AvatarImage src={m.author_picture} />
                <AvatarFallback className="bg-amber-400 text-slate-900 font-bold text-sm">
                  {m.author_name?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className={`max-w-[70%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                <div className="text-xs font-semibold text-slate-500 mb-1">
                  {mine ? "You" : m.author_name}
                </div>
                <div className={`px-4 py-2.5 rounded-2xl ${mine ? "bg-purple-700 text-white rounded-tr-sm" : "bg-slate-100 text-slate-900 rounded-tl-sm"}`}>
                  {m.reply_to && (
                    <div className={`mb-2 pl-2 border-l-2 rounded-sm text-xs ${mine ? "border-white/50 bg-white/10" : "border-purple-400 bg-purple-50"} py-1 pr-2`}>
                      <div className={`font-bold ${mine ? "text-white/90" : "text-purple-700"}`}>{m.reply_to.author_name}</div>
                      <div className={`truncate ${mine ? "text-white/80" : "text-slate-600"}`}>{m.reply_to.text}</div>
                    </div>
                  )}
                  {m.text && <p className="whitespace-pre-wrap break-words">{m.text}</p>}
                  {m.attachment_file_id && (
                    <AttachmentView fileId={m.attachment_file_id} mine={mine} />
                  )}
                  {yt && (
                    <div className="mt-2 rounded-xl overflow-hidden aspect-video">
                      <iframe src={yt} className="w-full h-full" allowFullScreen title="video" />
                    </div>
                  )}
                  {m.video_url && !yt && (
                    <a href={m.video_url} target="_blank" rel="noreferrer" className="underline text-xs">{m.video_url}</a>
                  )}
                </div>
                <div className={`flex items-center gap-3 mt-1 px-1 ${mine ? "flex-row-reverse" : ""}`}>
                  <button
                    type="button"
                    onClick={() => toggleLike(m.id)}
                    disabled={readOnly}
                    data-testid={`chat-like-${m.id}`}
                    className={`inline-flex items-center gap-1 text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${liked ? "text-red-500" : "text-slate-400 hover:text-red-500"}`}
                  >
                    <Heart className={`w-3.5 h-3.5 ${liked ? "fill-red-500" : ""}`} />
                    {likeCount > 0 && likeCount}
                  </button>
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => startReply(m)}
                      data-testid={`chat-reply-${m.id}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-slate-400 hover:text-purple-700 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <Reply className="w-3.5 h-3.5" /> Reply
                    </button>
                  )}
                  <span
                    data-testid={`chat-timestamp-${m.id}`}
                    title={new Date(m.created_at).toLocaleString()}
                    className="text-[11px] text-slate-400 font-medium"
                  >
                    {formatMessageTime(m.created_at)}
                  </span>
                </div>
              </div>
              {canDelete && (
                <button
                  type="button"
                  onClick={() => deleteMessage(m.id)}
                  data-testid={`chat-delete-message-${m.id}`}
                  title="Delete message (owner)"
                  className="self-center opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-600 p-1"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      <form onSubmit={send} className="relative z-10 border-t border-slate-100 p-4 space-y-2">
        {replyTo && (
          <div className="flex items-center gap-2 bg-purple-50 rounded-2xl px-3 py-2 text-sm border-l-4 border-purple-400" data-testid="chat-reply-banner">
            <Reply className="w-4 h-4 text-purple-700 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold text-purple-700">Replying to {replyTo.author_name}</div>
              <div className="truncate text-slate-600 text-xs">{replyTo.text || "attachment"}</div>
            </div>
            <button type="button" onClick={() => setReplyTo(null)} data-testid="chat-cancel-reply" className="text-slate-400 hover:text-slate-700">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {attachment && (
          <div className="flex items-center gap-2 bg-purple-50 rounded-2xl px-3 py-2 text-sm">
            <Paperclip className="w-4 h-4 text-purple-700" />
            <span className="truncate flex-1">{attachment.original_filename}</span>
            <button type="button" onClick={() => setAttachment(null)} className="text-slate-400 hover:text-slate-700" data-testid="remove-attachment-btn">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {showVideoInput && (
          <div className="flex items-center gap-2">
            <Input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="Paste YouTube link…" data-testid="chat-video-url" className="rounded-xl border-slate-200" />
            <Button type="button" variant="ghost" onClick={() => { setShowVideoInput(false); setVideoUrl(""); }} className="rounded-xl">
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" className="hidden" onChange={handleFile} data-testid="chat-file-input" />
          <Button type="button" variant="ghost" size="icon" onClick={() => fileRef.current?.click()} disabled={uploading || readOnly} data-testid="chat-attach-btn" className="rounded-full">
            <ImageIcon className="w-5 h-5 text-slate-500" />
          </Button>
          <Button type="button" variant="ghost" size="icon" onClick={() => setShowVideoInput((s) => !s)} disabled={readOnly} data-testid="chat-video-btn" className="rounded-full">
            <Video className="w-5 h-5 text-slate-500" />
          </Button>
          <Input
            ref={inputRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={readOnly ? "Read-only — pay your dues to chat" : "Type a message…"}
            disabled={readOnly}
            data-testid="chat-text-input"
            className="flex-1 h-12 rounded-2xl border-slate-200 disabled:opacity-60 disabled:cursor-not-allowed"
          />
          <Button type="submit" disabled={readOnly} data-testid="chat-send-btn" className="h-12 px-5 rounded-2xl bg-purple-700 hover:bg-purple-800 text-white btn-lift disabled:opacity-50">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </form>
    </div>
  );
}

function AttachmentView({ fileId, mine }) {
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    api.get(`/files/${fileId}`).then(({ data }) => setMeta(data)).catch(() => {});
  }, [fileId]);
  if (!meta) return null;
  if (meta.is_image) {
    return (
      <img src={fileSrc(fileId)} alt={meta.original_filename} className="mt-2 max-h-64 rounded-xl" />
    );
  }
  return (
    <a href={fileSrc(fileId)} target="_blank" rel="noreferrer"
       className={`mt-2 flex items-center gap-2 px-3 py-2 rounded-xl ${mine ? "bg-purple-900/40" : "bg-white"} text-sm`}>
      <Paperclip className="w-4 h-4" />
      <span className="truncate">{meta.original_filename}</span>
    </a>
  );
}
